/**
 * LiveFunnelAdmin
 *
 * Real-time, second-by-second activity for super-admin:
 *  - Live event feed (every event as it lands)
 *  - Active sessions (visitors with activity in the last 5 min)
 *  - Drop-off panel (where visitors get stuck without reaching checkout)
 *  - Event histogram
 *
 * Polls /api/admin/live-funnel every 3 seconds.
 */

import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useAdminAuth } from "@/hooks/useAdminAuth";
import { useCallback, useEffect, useRef, useState } from "react";
import { Activity, AlertTriangle, Clock, Loader2, RefreshCw, Users, Zap, ExternalLink } from "lucide-react";
import type { FunnelEventRow, ActiveSession, DropOffPoint, ArGeoBreakdown, LandingPathBreakdown } from "../../../api/admin/live-funnel";

interface LiveFunnelResponse {
  window_seconds: number;
  generated_at: string;
  total_events: number;
  active_sessions: ActiveSession[];
  feed: FunnelEventRow[];
  drop_off: DropOffPoint[];
  event_counts: { event_name: string; n: number }[];
  ar_breakdown?: ArGeoBreakdown[];
  landing_breakdown?: LandingPathBreakdown[];
  migration_pending?: string;
}

const LANDING_LABELS: Record<string, string> = {
  "/acceso": "Acceso (bio Instagram / linktree)",
  "/unete": "Únete (Stories ES)",
  "/vip": "VIP (DMs / cierre)",
  "/invitacion-premium": "Invitación Premium (canónico)",
};

function propsString(props: Record<string, unknown> | null, key: string): string | null {
  const v = props?.[key];
  return typeof v === "string" && v.trim() ? v.trim() : null;
}

const PH_PROJECT_ID = "422815";
const PH_UI = "https://us.posthog.com";

function FeedAnalyticsStrip({
  props,
  sessionId,
}: {
  props: Record<string, unknown> | null;
  sessionId?: string | null;
}) {
  const phClientSess =
    propsString(props, "posthog_client_session_id") ||
    (sessionId && !sessionId.startsWith("cs_") ? sessionId : null);
  let replay = propsString(props, "ph_session_replay_url");
  // Rewrite proxy/token URLs from older clients (`/ingest/project/phc_…/replay/…`).
  const replayOk =
    !!replay &&
    /^https:\/\//i.test(replay) &&
    !replay.includes("/ingest") &&
    !replay.includes("/project/phc_") &&
    /\/project\/\d+\/replay\//.test(replay);
  if (!replayOk && phClientSess) {
    replay = `${PH_UI}/project/${PH_PROJECT_ID}/replay/${encodeURIComponent(phClientSess)}`;
  }
  /** request id (legacy prop name vercel_request_id still accepted). */
  const requestId =
    propsString(props, "request_id") || propsString(props, "vercel_request_id");
  const brand = propsString(props, "brand_host");
  const mode = propsString(props, "build_mode");
  const pageClean = propsString(props, "page_url_clean");
  if (!replay && !requestId && !phClientSess && !brand && !mode && !pageClean) return null;

  return (
    <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] text-zinc-500">
      {replay && /^https?:\/\//i.test(replay) && (
        <a
          href={replay}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-0.5 text-sky-400 hover:text-sky-300 hover:underline"
        >
          Replay PH · {PH_PROJECT_ID}
          <ExternalLink className="h-2.5 w-2.5 opacity-80" aria-hidden />
        </a>
      )}
      {phClientSess && (
        <span className="font-mono text-zinc-500" title={phClientSess}>
          PH session{" "}
          <span className="text-zinc-400">
            {phClientSess.length > 14 ? `${phClientSess.slice(0, 14)}…` : phClientSess}
          </span>
        </span>
      )}
      {requestId && (
        <span className="font-mono text-zinc-600" title={requestId}>
          req{" "}
          <span className="text-zinc-500">
            {requestId.length > 20 ? `${requestId.slice(0, 20)}…` : requestId}
          </span>
        </span>
      )}
      {brand && (
        <span>
          host <span className="text-zinc-400">{brand}</span>
        </span>
      )}
      {mode && <span className="font-mono text-zinc-600">{mode}</span>}
      {pageClean && /^https?:\/\//i.test(pageClean) && (
        <a
          href={pageClean}
          target="_blank"
          rel="noopener noreferrer"
          className="max-w-[200px] truncate text-violet-400 hover:underline"
          title={pageClean}
        >
          URL limpia
        </a>
      )}
    </div>
  );
}

function geoInline(province: string | null, city: string | null, country: string | null): string | null {
  const bits = [province, city].filter((x) => x && String(x).trim());
  if (bits.length === 0) return country ? String(country).toUpperCase() : null;
  return `${bits.join(" · ")}${country && country.length === 2 ? ` (${country.toUpperCase()})` : ""}`;
}

const POLL_MS = 3_000;

function fmtTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleTimeString("es-AR", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
}

function ago(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const s = Math.max(0, Math.floor(ms / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${s % 60}s`;
  return `${Math.floor(m / 60)}h ${m % 60}m`;
}

function eventColor(name: string): string {
  if (name.includes("error") || name.includes("missing") || name.includes("failed")) return "text-red-400 bg-red-500/10";
  if (name.includes("checkout_initiated") || name.includes("checkout_created") || name.includes("plan_button_click") || name.includes("checkout_started")) return "text-emerald-400 bg-emerald-500/10";
  if (name.includes("view") || name.includes("opened")) return "text-blue-400 bg-blue-500/10";
  if (name.includes("closed") || name.includes("modal_closed") || name.includes("abandonment")) return "text-amber-400 bg-amber-500/10";
  if (name.includes("email_submitted") || name.includes("click")) return "text-purple-400 bg-purple-500/10";
  return "text-zinc-400 bg-zinc-500/10";
}

export default function LiveFunnelAdmin() {
  const { getAuthHeaders } = useAdminAuth();
  const [data, setData] = useState<LiveFunnelResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [windowSec, setWindowSec] = useState(300);
  const [lastFetch, setLastFetch] = useState<Date | null>(null);
  const [autoTick, setAutoTick] = useState(0); // forces re-render every second for "ago" labels
  const getAuthRef = useRef(getAuthHeaders);
  getAuthRef.current = getAuthHeaders;

  const fetchData = useCallback(async (silent = false) => {
    if (!silent) {
      setLoading(true);
      setError(null);
    }
    try {
      const headers = await getAuthRef.current();
      const res = await fetch(`/api/admin/live-funnel?seconds=${windowSec}&limit=100`, { headers });
      if (!res.ok) {
        const body = await res.text();
        throw new Error(`HTTP ${res.status} — ${body.slice(0, 150)}`);
      }
      const json = (await res.json()) as LiveFunnelResponse;
      setData(json);
      setLastFetch(new Date());
      if (silent) setError(null);
    } catch (err) {
      if (!silent) setError(err instanceof Error ? err.message : String(err));
    } finally {
      if (!silent) setLoading(false);
    }
  }, [windowSec]);

  useEffect(() => { void fetchData(); }, [fetchData]);

  useEffect(() => {
    const id = window.setInterval(() => void fetchData(true), POLL_MS);
    return () => window.clearInterval(id);
  }, [fetchData]);

  // Re-render every second for live "Xs ago" labels
  useEffect(() => {
    const id = window.setInterval(() => setAutoTick((n) => n + 1), 1000);
    return () => window.clearInterval(id);
  }, []);
  // Anchor to make eslint happy that we use autoTick
  void autoTick;

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <AdminPageHeader
        description="Cada evento de cada visitante en tiempo real. Quién entra, dónde se traba, qué falla."
        actions={
          <div className="flex items-center gap-2">
            <div className="hidden items-center gap-2 text-xs text-muted-foreground sm:flex">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
              </span>
              <span>Live · cada {POLL_MS / 1000}s {lastFetch && <>· {fmtTime(lastFetch.toISOString())}</>}</span>
            </div>
            <Button onClick={() => void fetchData()} disabled={loading} variant="outline" size="sm" type="button">
              <RefreshCw className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`} />
              Actualizar
            </Button>
          </div>
        }
      />

      {/* Window selector */}
      <div className="flex items-center gap-2 text-xs">
        <span className="text-muted-foreground mr-2">Ventana:</span>
        {[60, 300, 900, 1800, 3600].map((s) => (
          <button
            key={s}
            onClick={() => setWindowSec(s)}
            className={`rounded px-2 py-1 ${windowSec === s ? "bg-primary text-primary-foreground" : "bg-muted hover:bg-muted/70"}`}
          >
            {s < 60 ? `${s}s` : s < 3600 ? `${Math.floor(s / 60)}m` : `${Math.floor(s / 3600)}h`}
          </button>
        ))}
      </div>

      {error && (
        <Card className="border-red-500/30 bg-red-50/40 dark:bg-red-950/20">
          <CardContent className="pt-6">
            <div className="flex gap-3">
              <AlertTriangle className="h-5 w-5 text-red-600" />
              <div className="text-sm">
                <p className="font-medium text-red-700 dark:text-red-300">Error</p>
                <code className="text-xs text-red-600">{error}</code>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {loading && !data ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : data ? (
        <>
          {data.migration_pending && (
            <Card className="border-amber-500/40 bg-amber-500/5">
              <CardContent className="pt-6 text-sm text-amber-300">
                Migración pendiente: <code className="font-mono">{data.migration_pending}</code>. Va a aplicarse en el próximo deploy; mientras tanto este panel está vacío.
              </CardContent>
            </Card>
          )}

          {/* Top-line counters */}
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <Card>
              <CardHeader className="pb-2">
                <CardDescription className="flex items-center gap-1 text-xs">
                  <Activity className="h-3 w-3" /> Eventos en ventana
                </CardDescription>
                <CardTitle className="text-2xl tabular-nums">{data.feed.length}</CardTitle>
              </CardHeader>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardDescription className="flex items-center gap-1 text-xs">
                  <Users className="h-3 w-3 text-emerald-400" /> Sesiones activas
                </CardDescription>
                <CardTitle className="text-2xl tabular-nums text-emerald-400">{data.active_sessions.length}</CardTitle>
              </CardHeader>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardDescription className="flex items-center gap-1 text-xs">
                  <Zap className="h-3 w-3 text-blue-400" /> Llegaron a checkout
                </CardDescription>
                <CardTitle className="text-2xl tabular-nums text-blue-400">
                  {data.active_sessions.filter((s) => s.reached_checkout).length}
                </CardTitle>
              </CardHeader>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardDescription className="flex items-center gap-1 text-xs">
                  <AlertTriangle className="h-3 w-3 text-amber-400" /> Trabados
                </CardDescription>
                <CardTitle className="text-2xl tabular-nums text-amber-400">
                  {data.drop_off.reduce((sum, d) => sum + d.stuck_sessions, 0)}
                </CardTitle>
              </CardHeader>
            </Card>
          </div>

          {/* Abandonment signals — checkout errors + panel closes in window */}
          {data.event_counts && data.event_counts.some((e) =>
            e.event_name.includes("error") ||
            e.event_name.includes("abandonment") ||
            e.event_name === "checkout_panel_closed" ||
            e.event_name === "stripe_pay_failure" ||
            e.event_name.includes("blocked") ||
            e.event_name.includes("timeout") ||
            e.event_name.includes("failed")
          ) && (
            <Card className="border-orange-500/30 bg-orange-500/5 dark:bg-orange-950/20">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2 text-orange-500">
                  <AlertTriangle className="h-4 w-4" /> Señales de abandono en ventana
                </CardTitle>
                <CardDescription>
                  Eventos de error, abandono y cierre de checkout detectados en la ventana actual.
                  Altas frecuencias indican fricciones a resolver.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {data.event_counts
                    .filter((e) =>
                      e.event_name.includes("error") ||
                      e.event_name.includes("abandonment") ||
                      e.event_name === "checkout_panel_closed" ||
                      e.event_name === "stripe_pay_failure" ||
                      e.event_name.includes("blocked") ||
                      e.event_name.includes("timeout") ||
                      e.event_name.includes("failed")
                    )
                    .map((e) => (
                      <div key={e.event_name} className="flex items-center gap-1.5 rounded-md border border-orange-500/20 bg-orange-500/10 px-2 py-1 text-xs">
                        <span className={`rounded px-1.5 py-0.5 text-[10px] ${eventColor(e.event_name)}`}>
                          {e.event_name}
                        </span>
                        <span className="font-bold tabular-nums text-orange-400">{e.n}</span>
                      </div>
                    ))}
                </div>
              </CardContent>
            </Card>
          )}

          <div className="grid gap-6 md:grid-cols-2">
            {/* Active sessions */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Users className="h-4 w-4" /> Sesiones activas
                </CardTitle>
                <CardDescription>Visitantes con eventos en los últimos 5 min</CardDescription>
              </CardHeader>
              <CardContent className="overflow-y-auto max-h-[480px]">
                {data.active_sessions.length === 0 ? (
                  <p className="text-xs text-muted-foreground">Nadie navegando ahora.</p>
                ) : (
                  <div className="space-y-2">
                    {data.active_sessions.map((s) => (
                      <div
                        key={s.session_id}
                        className={`rounded-md border p-2 text-xs ${s.reached_checkout ? "border-emerald-500/30 bg-emerald-500/5" : "border-zinc-800 bg-zinc-900/30"}`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-mono text-[10px] text-zinc-500">
                            {s.session_id.slice(0, 8)}…
                          </span>
                          <div className="flex items-center gap-1 text-[10px] text-zinc-500">
                            <Clock className="h-3 w-3" />
                            <span className="tabular-nums">hace {ago(s.last_seen)}</span>
                          </div>
                        </div>
                        <div className="mt-1 flex items-center justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            {s.customer_email ? (
                              <p className="truncate text-zinc-200">{s.customer_email}</p>
                            ) : (
                              <p className="text-zinc-500">Anónimo {s.country && `(${s.country})`}</p>
                            )}
                            <p className="truncate text-[10px] text-zinc-500">
                              {s.template ?? "—"} · {s.flow_name ?? "—"}
                            </p>
                            {s.landing_path && (
                              <p className="truncate font-mono text-[10px] text-zinc-600" title={s.landing_path}>
                                {s.landing_path}
                              </p>
                            )}
                            {(() => {
                              const geoLabel = geoInline(
                                s.province ?? null,
                                s.city ?? null,
                                s.country ?? null,
                              );
                              return geoLabel ? (
                                <p className="truncate text-[10px] text-zinc-600">{geoLabel}</p>
                              ) : null;
                            })()}
                            {s.posthog_distinct_id && (
                              <p
                                className="truncate font-mono text-[10px] text-amber-200/80"
                                title={s.posthog_distinct_id}
                              >
                                posthog {s.posthog_distinct_id}
                              </p>
                            )}
                          </div>
                          <span className="tabular-nums text-[10px] text-zinc-400">
                            {s.events_count} ev · {s.duration_s}s
                          </span>
                        </div>
                        <div className="mt-1 flex items-center gap-1">
                          <span className={`rounded px-1.5 py-0.5 text-[10px] ${eventColor(s.last_event)}`}>
                            {s.last_event}
                          </span>
                          {s.reached_checkout && <span className="text-[10px] text-emerald-400">→ checkout</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Drop-off */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2 text-amber-400">
                  <AlertTriangle className="h-4 w-4" /> Drop-off
                </CardTitle>
                <CardDescription>
                  Última acción de las sesiones que <strong>no</strong> llegaron a checkout.
                </CardDescription>
              </CardHeader>
              <CardContent className="overflow-y-auto max-h-[480px]">
                {data.drop_off.length === 0 ? (
                  <p className="text-xs text-muted-foreground">Sin abandonos en la ventana.</p>
                ) : (
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b text-left text-muted-foreground">
                        <th className="pb-2 pr-2">Último evento</th>
                        <th className="pb-2 pr-2">Template</th>
                        <th className="pb-2 pr-2">Flow</th>
                        <th className="pb-2 pr-2">País</th>
                        <th className="pb-2 text-right">Sesiones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.drop_off.map((d, i) => (
                        <tr key={`${d.last_event}-${i}`} className="border-b last:border-0 hover:bg-muted/30">
                          <td className="py-1.5 pr-2">
                            <span className={`rounded px-1.5 py-0.5 text-[10px] ${eventColor(d.last_event)}`}>
                              {d.last_event}
                            </span>
                          </td>
                          <td className="py-1.5 pr-2 truncate max-w-[100px]">{d.template ?? "—"}</td>
                          <td className="py-1.5 pr-2 font-mono text-[10px] truncate max-w-[100px]">{d.flow_name ?? "—"}</td>
                          <td className="py-1.5 pr-2">{d.country ?? "—"}</td>
                          <td className="py-1.5 text-right tabular-nums font-semibold text-amber-400">{d.stuck_sessions}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Landing-path breakdown — entries to the funnel by URL */}
          {data.landing_breakdown && data.landing_breakdown.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <span>🔗</span> Entradas al funnel por landing
                </CardTitle>
                <CardDescription>
                  Las cuatro URLs AR-compartibles (<code className="font-mono text-[10px]">/acceso</code>, <code className="font-mono text-[10px]">/unete</code>, <code className="font-mono text-[10px]">/vip</code>, <code className="font-mono text-[10px]">/invitacion-premium</code>) renderean el mismo template — esta tabla las separa.
                </CardDescription>
              </CardHeader>
              <CardContent className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b text-left text-muted-foreground">
                      <th className="pb-2 pr-3">Landing path</th>
                      <th className="pb-2 pr-3">Etiqueta</th>
                      <th className="pb-2 pr-3">País</th>
                      <th className="pb-2 pr-3 text-right">Eventos</th>
                      <th className="pb-2 pr-3 text-right">Sesiones</th>
                      <th className="pb-2 pr-3 text-right text-emerald-400">→ Click checkout</th>
                      <th className="pb-2 text-right">Conv. rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.landing_breakdown.map((row, i) => (
                      <tr key={`${row.landing_path}-${i}`} className="border-b last:border-0 hover:bg-muted/30">
                        <td className="py-1.5 pr-3 font-mono text-[10px]">{row.landing_path}</td>
                        <td className="py-1.5 pr-3">{LANDING_LABELS[row.landing_path] ?? <span className="text-muted-foreground">—</span>}</td>
                        <td className="py-1.5 pr-3">{row.country ?? "—"}</td>
                        <td className="py-1.5 pr-3 text-right tabular-nums">{row.events}</td>
                        <td className="py-1.5 pr-3 text-right tabular-nums">{row.sessions}</td>
                        <td className="py-1.5 pr-3 text-right tabular-nums text-emerald-400">{row.reached_checkout}</td>
                        <td className={`py-1.5 text-right tabular-nums font-semibold ${row.conv_rate === 0 ? "text-zinc-500" : row.conv_rate >= 0.1 ? "text-emerald-400" : "text-amber-400"}`}>
                          {(row.conv_rate * 100).toFixed(1)}%
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          )}

          {/* Argentina province / city breakdown */}
          {data.ar_breakdown && data.ar_breakdown.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <span>🇦🇷</span> Tráfico AR por provincia / ciudad
                </CardTitle>
                <CardDescription>
                  Visitantes AR en la ventana, agrupados por provincia + ciudad/barrio (geo Cloudflare:
                  <code className="mx-1 font-mono text-[10px]">cf.region</code> /
                  <code className="mx-1 font-mono text-[10px]">cf.city</code>
                  ). Usá esto para decidir qué CTA prender por barrio en{" "}
                  <a href="/admin/wishlist-config" className="underline hover:text-foreground">
                    wishlist-config
                  </a>
                  . CTR = % de sesiones que llegaron a un evento de checkout.
                </CardDescription>
              </CardHeader>
              <CardContent className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b text-left text-muted-foreground">
                      <th className="pb-2 pr-3">Provincia</th>
                      <th className="pb-2 pr-3">Ciudad</th>
                      <th className="pb-2 pr-3 text-right">Eventos</th>
                      <th className="pb-2 pr-3 text-right">Sesiones</th>
                      <th className="pb-2 pr-3 text-right text-emerald-400">→ Click checkout</th>
                      <th className="pb-2 text-right">CTR</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.ar_breakdown.map((row, i) => {
                      const ctr = row.sessions > 0 ? row.reached_checkout / row.sessions : 0;
                      return (
                        <tr key={`${row.province}-${row.city}-${i}`} className="border-b last:border-0 hover:bg-muted/30">
                          <td className="py-1.5 pr-3 font-medium">{row.province ?? <span className="text-muted-foreground">(sin provincia)</span>}</td>
                          <td className="py-1.5 pr-3">{row.city ?? <span className="text-muted-foreground">—</span>}</td>
                          <td className="py-1.5 pr-3 text-right tabular-nums">{row.events}</td>
                          <td className="py-1.5 pr-3 text-right tabular-nums">{row.sessions}</td>
                          <td className="py-1.5 pr-3 text-right tabular-nums text-emerald-400">{row.reached_checkout}</td>
                          <td className={`py-1.5 text-right tabular-nums font-semibold ${ctr === 0 ? "text-zinc-500" : ctr >= 0.1 ? "text-emerald-400" : "text-amber-400"}`}>
                            {(ctr * 100).toFixed(1)}%
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <p className="mt-3 text-[10px] text-muted-foreground">
                  Geo CF/Worker: provincia + ciudad/barrio (cf.city). Prioridad CTA:{" "}
                  <code className="font-mono">city_ar</code> &gt; provincia.{" "}
                  Cambiar qué ven:{" "}
                  <a href="/admin/wishlist-config" className="underline hover:text-foreground">
                    /admin/wishlist-config
                  </a>
                  {" "}→ Guardar → live en ~2–5 s (KV version, sin redeploy).
                </p>
              </CardContent>
            </Card>
          )}

          {/* Live feed */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Activity className="h-4 w-4" /> Feed en vivo
              </CardTitle>
              <CardDescription>
                Cada evento que registramos, segundo a segundo. Más reciente arriba.
              </CardDescription>
            </CardHeader>
            <CardContent className="overflow-y-auto max-h-[600px]">
              {data.feed.length === 0 ? (
                <p className="text-xs text-muted-foreground">Sin actividad en la ventana.</p>
              ) : (
                <div className="space-y-2 font-mono text-[11px]">
                  {data.feed.map((ev) => {
                    const geoLabel = geoInline(ev.province ?? null, ev.city ?? null, ev.country ?? null);
                    const who = ev.customer_email
                      ? ev.customer_email
                      : ev.posthog_distinct_id
                        ? `posthog:${ev.posthog_distinct_id}`
                        : ev.session_id
                          ? `sess:${ev.session_id.slice(0, 10)}…`
                          : "anon";
                    return (
                      <div
                        key={ev.id}
                        className="border-b border-zinc-900 py-2 last:border-0"
                      >
                        <div className="flex flex-wrap items-start gap-x-2 gap-y-1">
                          <span className="shrink-0 tabular-nums text-zinc-500 w-[60px]">
                            {fmtTime(ev.created_at)}
                          </span>
                          <span className={`shrink-0 rounded px-1.5 py-0.5 ${eventColor(ev.event_name)}`}>
                            {ev.event_name}
                          </span>
                          <span className="shrink-0 text-zinc-500">
                            {ev.template ?? "—"} · {ev.flow_name ?? "—"}
                          </span>
                          {geoLabel && (
                            <span className="shrink-0 text-[10px] text-zinc-500 truncate max-w-[180px]" title={geoLabel}>
                              {geoLabel}
                            </span>
                          )}
                          <span className="min-w-0 flex-1 truncate text-zinc-400 text-[10px] sm:text-[11px]" title={who}>
                            {who}
                          </span>
                        </div>
                        {(ev.landing_path || ev.template_variant) && (
                          <div className="mt-1 pl-0 sm:pl-[60px] flex flex-wrap gap-x-2 text-[10px] text-zinc-600">
                            {ev.landing_path && (
                              <span className="font-mono" title={ev.landing_path}>
                                {ev.landing_path}
                              </span>
                            )}
                            {ev.template_variant && (
                              <span className="text-zinc-500">variante {ev.template_variant}</span>
                            )}
                          </div>
                        )}
                        <div className="mt-1 pl-0 sm:pl-[60px]">
                          <FeedAnalyticsStrip props={ev.props} sessionId={ev.session_id} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </>
      ) : null}
    </div>
  );
}
